import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card } from "antd";
import {
  FiFileText,
  FiUsers,
  FiCheckCircle,
  FiDollarSign,
  FiBarChart2,
} from "react-icons/fi";
import { SiBlockbench } from "react-icons/si";
import moment from "moment";

const COLORS = ["#0DCCCC", "#F18E57", "#6889f2"];
const OUTER_COLORS = ["#85c1e9", "#2e86c1"];

const BillableSunburstGraph = ({ dataset }) => {
  const checkKeys = [
    "BillableEmployees",
    "NonBillableEmployees",
    "SupportEmployees",
    "AvailableCount",
    "NotAvailableCount",
  ];

  const isEmptyData =
    !dataset || checkKeys.every((key) => !dataset[key] || dataset[key] == 0);

  const billableData = [
    { name: "Support", value: Number(dataset?.SupportEmployees) || 0 },
    { name: "Billable", value: Number(dataset?.BillableEmployees) || 0 },
    { name: "Non-Billable", value: Number(dataset?.NonBillableEmployees) || 0 },
  ];

  const totalInner = billableData.reduce((sum, item) => sum + item.value, 0);
  const nonBillableFraction = dataset?.NonBillableEmployees / totalInner || 0;

  const totalAvailability = dataset.NotAvailableCount + dataset.AvailableCount;
  const nonBillableCount = dataset.NonBillableEmployees || 1;

  const supportFraction = (dataset?.SupportEmployees || 0) / totalInner;
  const billableFraction = (dataset?.BillableEmployees || 0) / totalInner;

  const startAngleOuter = (supportFraction + billableFraction) * 360;

  const availabilityData = [
    {
      name: "Not Available",
      value: dataset.NotAvailableCount,
      scaledValue:
        (dataset.NotAvailableCount / totalAvailability) * nonBillableCount,
    },
    {
      name: "Available",
      value: dataset.AvailableCount,
      scaledValue:
        (dataset.AvailableCount / totalAvailability) * nonBillableCount,
    },
  ];

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
    name,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${value}`}
      </text>
    );
  };

  const renderOuterLabel = ({ cx, cy, midAngle, outerRadius, payload }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius - 12; // Adjust label position outside the outer graph
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${payload.value}`} {/* Show actual count */}
      </text>
    );
  };

  const getWeekdaysInPreviousMonth = () => {
    const prevMonth = moment().subtract(1, "months");
    const daysInMonth = prevMonth.daysInMonth();

    let weekdaysCount = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDay = prevMonth.date(day);
      const dayOfWeek = currentDay.day();

      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        weekdaysCount++;
      }
    }

    return weekdaysCount;
  };

  const listStyle = {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    padding: "5px 0",
    borderBottom: "1px solid #eee",
  };

  const iconStyle = {
    marginRight: "8px",
    fontSize: "16px",
    color: "#1976D2",
  };

  return (
    <Card
      style={{
        border: "1px solid #0000001F",
        borderRadius: "16px",
        position: "relative",
      }}
    >
      <div
        style={{
          fontWeight: "700",
          fontSize: "16px",
          padding: "0px 8px 4px 8px",
        }}
      >
        Workforce Engagement Analysis
      </div>
      {isEmptyData ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            height: "332px",
            color: "#9B9DA0",
            fontWeight: "700",
          }}
        >
          <span>
            <FiFileText style={{ fontSize: "60px" }} />
          </span>
          <span>No data available</span>
        </div>
      ) : (
        <div
          style={{
            height: "332px",
            display: "flex",
            justifyContent: "space-between",
            padding: "2px",
          }}
        >
          <div style={{ width: "50%" }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={billableData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={90}
                  fill="#82ca9d"
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {billableData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>

                {dataset?.NonBillableEmployees > 0 && (
                  <Pie
                    data={availabilityData}
                    dataKey="scaledValue"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    startAngle={startAngleOuter}
                    endAngle={startAngleOuter + nonBillableFraction * 360}
                    innerRadius={91}
                    outerRadius={140}
                    fill="#8884d8"
                    label={renderOuterLabel}
                    labelLine={false}
                  >
                    {availabilityData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={OUTER_COLORS[index]} />
                    ))}
                  </Pie>
                )}

                <Tooltip
                  formatter={(value, name, props) => {
                    if (
                      props.payload &&
                      props.payload.payload.value !== undefined
                    ) {
                      return [`${props.payload.payload.value}`, name];
                    }
                    return [`${value}`, name];
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  align="left"
                  wrapperStyle={{
                    fontSize: "12px",
                    fontWeight: "normal",
                    paddingTop: "25px",
                    display: "flex",
                    justifyContent: "left",
                    marginLeft: "5px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <ul style={{ listStyle: "none", paddingLeft: "9px", margin: "0" }}>
              <li style={listStyle}>
                <FiDollarSign style={iconStyle} />
                <strong>Billability :</strong>&nbsp;
                {Number(dataset.BillableEmployees)}/
                {Number(dataset.NonSupportEmployees)}&nbsp;&nbsp;(
                {(
                  (Number(dataset.BillableEmployees) /
                    Number(dataset.NonSupportEmployees)) *
                  100
                ).toFixed(2)}
                %)
              </li>
              <li style={listStyle}>
                <FiBarChart2 style={iconStyle} color="#ff9800" />
                <strong>Utilization % :</strong>&nbsp;
                {(
                  (Number(dataset.TotalPreviousMonthWorked) /
                    60 /
                    (getWeekdaysInPreviousMonth() *
                      8 *
                      Number(dataset.NonSupportEmployees))) *
                  100
                ).toFixed(2)}
                %
              </li>
              <li style={listStyle}>
                <SiBlockbench style={iconStyle} />
                <strong>Bench % :</strong>&nbsp;
                {Number(dataset.OnBenchEmployees)}/
                {Number(dataset.NonSupportEmployees)}&nbsp;&nbsp;(
                {(
                  (Number(dataset.OnBenchEmployees) /
                    Number(dataset.NonSupportEmployees)) *
                  100
                ).toFixed(2)}
                %)
              </li>
              <li style={listStyle}>
                <FiCheckCircle style={iconStyle} />
                <strong>Availability of Bench :</strong>&nbsp;
                {Number(dataset.AvailableCount)}/
                {Number(dataset.NonBillableEmployees)}&nbsp;&nbsp;(
                {(
                  (Number(dataset.AvailableCount) /
                    Number(dataset.NonBillableEmployees)) *
                  100
                ).toFixed(2)}
                %)
              </li>
              <li style={listStyle}>
                <FiUsers style={iconStyle} />
                <strong>Support to Engineering :</strong>&nbsp;
                {Number(dataset.SupportEmployees)}/
                {Number(dataset.NonSupportEmployees)}&nbsp;&nbsp;(
                {(
                  (Number(dataset.SupportEmployees) /
                    Number(dataset.NonSupportEmployees)) *
                  100
                ).toFixed(2)}
                %)
              </li>
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BillableSunburstGraph;
